// Drawings (ink): colours, sizes and tools of the drawing bar (kept per browser) and a hit test.
// The strokes themselves live in the engine (lanewerk-core board/ink.rs).
import type { Stroke } from "./engine-api";
import type { RGB } from "./palette";

/** Ink colours; index = stored colour (as many as INK_COLOR_COUNT in ink.rs). */
export const INK_COLORS: { name: string; rgb: RGB }[] = [
  { name: "Navy", rgb: [0, 35, 75] }, { name: "Blau", rgb: [19, 118, 191] }, { name: "Cyan", rgb: [2, 170, 220] },
  { name: "Rot", rgb: [214, 69, 69] }, { name: "Grün", rgb: [0, 160, 110] }, { name: "Orange", rgb: [240, 140, 20] },
  { name: "Violett", rgb: [124, 77, 160] },
];
export const MAX_INK_POINTS = 4000;
export type InkTool = "pen" | "marker" | "eraser";
export interface InkSettings { tool: InkTool; color: number; size: number }
export const INK_KEY = "lanewerk.ink";
export const INK_SIZES = ["Fein", "Mittel", "Dick"];

const icon = (paths: string) => `<svg viewBox="0 0 16 16" aria-hidden="true">${paths}</svg>`;
/** The tools of the drawing bar: id, label, icon (static markup). */
export const INK_TOOLS: [InkTool, string, string][] = [
  ["pen", "Stift", icon('<path d="M10.8 2.2l3 3-8.1 8.1-3.6.6.6-3.6z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>')],
  ["marker", "Textmarker", icon('<path d="M9.6 2.4l4 4-5.4 5.4H4.2V7.8z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M2 14.2h8" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" opacity=".45"/>')],
  ["eraser", "Radierer", icon('<path d="M6.2 13.4L2.7 9.9a1.4 1.4 0 010-2l5.2-5.2a1.4 1.4 0 012 0l3.4 3.4a1.4 1.4 0 010 2l-5.4 5.3z" fill="none" stroke="currentColor" stroke-width="1.6"/><path d="M5 7.6l4.4 4.4M6.4 13.6H14" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>')],
];

export function loadInk(): InkSettings {
  try {
    const v = JSON.parse(localStorage.getItem(INK_KEY) ?? "null") as Partial<InkSettings> | null;
    if (v && (v.tool === "pen" || v.tool === "marker" || v.tool === "eraser")) {
      return { tool: v.tool, color: Math.min(INK_COLORS.length - 1, Math.max(0, Number(v.color) | 0)), size: Math.min(2, Math.max(0, Number(v.size) | 0)) };
    }
  } catch { /* storage blocked */ }
  return { tool: "pen", color: 0, size: 1 };
}

export function saveInk(st: InkSettings) {
  try { localStorage.setItem(INK_KEY, JSON.stringify(st)); } catch { /* not kept */ }
}

/** Width (board px) of a new point: the pen draws 2 / 4 / 8 screen px whatever the zoom, with a
 *  pencil following the pressure; the marker 14 / 24 / 36. */
export function inkWidth(st: InkSettings, pressure: number, pointerType: string, zoom: number): number {
  if (st.tool === "marker") return [14, 24, 36][st.size] / zoom;
  const p = pointerType === "pen" && pressure > 0 ? pressure : 0.5;
  return ([2, 4, 8][st.size] * (0.4 + 1.2 * p)) / zoom;
}

/** Whether the stroke's line passes within `tol` (board px) of the point. */
export function strokeHit(s: Stroke, x: number, y: number, tol: number): boolean {
  if (x < s.x - tol || y < s.y - tol || x > s.x + s.w + tol || y > s.y + s.h + tol) return false;
  const p = s.pts;
  for (let i = 0; i < p.length; i += 3) {
    const r = tol + p[i + 2] / 2;
    if (i + 3 >= p.length) {
      if (Math.hypot(x - p[i], y - p[i + 1]) <= r) return true;
      continue;
    }
    const ax = p[i], ay = p[i + 1], dx = p[i + 3] - ax, dy = p[i + 4] - ay, l = dx * dx + dy * dy;
    const t = l ? Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / l)) : 0;
    if (Math.hypot(x - (ax + t * dx), y - (ay + t * dy)) <= r) return true;
  }
  return false;
}
